import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@mui/styles';
import { alpha } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import _ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import ViewsIcon from '@mui/icons-material/DynamicFeed';
import PoliciesIcon from '@mui/icons-material/Policy';
import SensorsIcon from '@mui/icons-material/Assessment';
import SystemIcon from '@mui/icons-material/Settings';

import AlertsIcon from '@mui/icons-material/Notifications';
import MetadataIcon from '@mui/icons-material/Apps';
import PacketCaptureIcon from '@mui/icons-material/SettingsEthernet';
import UsersIcon from '@mui/icons-material/Person';
import ConfigurationIcon from '@mui/icons-material/SettingsApplications';
import CMCHealthIcon from '@mui/icons-material/Healing';
import AuditIcon from '@mui/icons-material/Subject';
import ReportsIcon from '@mui/icons-material/Assignment';
import UpgradeIcon from '@mui/icons-material/Update';

import Auth from '../../data/auth-service';
import DataContext from '../../data/data-context';
import Permissions from '../../data/permissions';

const drawerWidth = 300;
const drawerWidthCollapsed = '60px !important';

const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        color: theme.palette.text.primary,

        '& .MuiDrawer-paperAnchorDockedLeft': {
            borderStyle: 'none',
        },
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        // scrollbarWidth: 'none',
        transition: 'width 500ms',
    },
    drawerCollapsed: {
        width: drawerWidthCollapsed,
        transition: 'width 500ms',
    },
    drawerPaper: {
        width: drawerWidth,
        transition: 'width 500ms',
        // '&::-webkit-scrollbar': {
        //     display: 'none',
        // },
        // '-ms-overflow-style': 'none',
        scrollbarWidth: 'none'
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        minHeight: 85,
        ...theme.mixins.toolbar,
    },
    list: {
        flex: 1,
        width: '100%',
        minWidth: 275,
        maxWidth: 300,
        marginTop: 12,
        backgroundColor: theme.palette.nav.paperAlt,
        color: theme.palette.common.white,
        '& .MuiListItem-button': {
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
            paddingTop: '10px',
            paddingBottom: '10px',
            minHeight: 53,
        },
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none'
    },
    listSubheader: {
        color: theme.palette.common.white,
    },
    nested: {
        paddingLeft: theme.spacing(4),
        backgroundColor: theme.palette.nav.nested,

        '&:hover': {
            // text-decoration: none;
            backgroundColor: `${alpha(theme.palette.common.white, 0.08)}`,
        },
    },
    closeButton: {
        color: theme.palette.common.white,
        float: 'right',
        padding: '6px 0',
        minWidth: '24px',
    },
    selected: {
        background: theme.palette.nav.selection,
        borderRight: `9px solid ${theme.palette.nav.primary}`,
    },
    customIcon: {
        width: 22,
        height: 22,
    },
    secondaryColor: {
        color: theme.palette.secondary.main,
    },
    sensorErrors: {
        background: theme.palette.error.main,
        padding: 2,
        borderRadius: 30,
        width: 30,
        color: theme.palette.error.contrastText,
        textAlign: 'center',
        fontSize: 17,
    },
    sidebarBottomSpacer: {
        height: '48px',
    },
}));

// show/hide ListItem based on permissions
const ListItem = ({ noCheck, perm, children, ...rest }) => {
    const p = !perm ? {} : Object.prototype.toString.call(perm).includes('Object') ? perm : {};
    return (noCheck || Auth.perm(p))
        ? <_ListItem {...rest}> { children } </_ListItem>
        : null;
};
ListItem.propTypes = {
    perm: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    children: PropTypes.node,
    noCheck: PropTypes.bool,
};

const SideNav = (props) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const { systemHealth, sidenav: _sidenav, setSidenav: _setSidenav } = useContext(DataContext);
    // keep a copy in a local state for faster response
    const [sidenav, setSidenav] = useState(DataContext);

    const handleLink = link => {
        navigate(link);
    };

    const setPanels = useCallback(panels => {
        // once for us for fast feedback and once for the rest of the app.
        setSidenav(current => {
            if (!current) return { panels };
            current.panels = panels;
            return current;
        });
        _setSidenav({ panels });
    }, [_setSidenav]);

    useEffect(() => {
        const locData = location.pathname.split('/').filter(v => Boolean(v));
        if (locData.length) {
            setPanels({
                [locData[0]]: true,
            });
        }
    }, [location]);

    useEffect(() => {
        setSidenav(_sidenav);
    }, [_sidenav]);

    return (
        <div className={classes.root}>
            <Drawer
                open={true}
                className={clsx(classes.drawer, { [classes.drawerCollapsed]: sidenav.collapsed })}
                variant="persistent"
                classes={{
                    paper: clsx(classes.drawerPaper, { [classes.drawerCollapsed]: sidenav.collapsed }),
                }}
            >
                <div className={classes.toolbar} />
                <List component="nav" className={classes.list} aria-label="Side Nav Bar" style={props.header ? { marginTop: '42px' } : {}}>
                    <ListItem
                        noCheck
                        button
                        onClick={() => { handleLink('/license/generate') }}
                        className={clsx({ [classes.selected]: (props.path.match('license/generate')) })}
                        aria-label="Open Generate License page"
                    >
                        <ListItemIcon><ViewsIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('license/generate') })} /></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="License"></ListItemText>
                    </ListItem>
                    <ListItem
                        noCheck
                        button
                        onClick={() => { handleLink('/reports') }}
                        className={clsx({ [classes.selected]: (props.path.match('reports')) })}
                        aria-label="Open Reports page"
                    >
                        <ListItemIcon><PoliciesIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('reports') })} /></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Reports"></ListItemText>
                    </ListItem>
                    <ListItem
                        noCheck
                        button
                        onClick={() => { handleLink('/users') }}
                        className={clsx({ [classes.selected]: (props.path.match('users')) })}
                        aria-label="Open Users page"
                    >
                        <ListItemIcon><SensorsIcon className={clsx(classes.listSubheader, { [classes.secondaryColor]: props.path.match('users') })} /></ListItemIcon>
                        <ListItemText style={{ display: (sidenav.collapsed ? 'none' : 'block') }} primary="Users"></ListItemText>
                    </ListItem>
                </List>
            </Drawer>
        </div>
    );
};
SideNav.propTypes = {
    path: PropTypes.string,
    header: PropTypes.bool,
};

export default SideNav;
